import { CaretDownOutlined, CaretUpOutlined } from '@ant-design/icons'
import type { ViewFieldsFragment } from '@salescore/client-api'
import {
  CORE_CONSTANT,
  getInnerRowSpan,
  type SheetCell,
  type ViewQueryList,
  type ViewQueryRecordNode,
  type ViewQueryRecordNodeWithId,
} from '@salescore/core'
import { HashMap, Option } from 'effect'
import type { ReactElement } from 'react'

import type { SheetColumn, SheetSystemColumn } from '../../../domain/column'
import type { RSheetsCursorPosition } from '../../../domain/cursor/cursorTypes'
import type { LatestFieldChangeItem } from '../../../recoil/recordsHistory'
import type { RowConfig } from '../../../recoil/rowConfig'
import type { SalescoreObjectAbility, ViewAbility } from '../../../recoil/view/atoms'
import { getHeaderColor } from '../../../rsheet/components/header/RSheetsNodeBlockHeader.tsx'
import type { SetMainCursorByRecordIdArguments } from '../../../rsheet/hooks/useSetMainCursorByRecordId.ts'
import { RSheetsStyle } from '../../../rsheet/util/RSheetsStyle.ts'
import { SheetSystemCell } from './SheetSystemCell.tsx'
import { SheetValueCell } from './SheetValueCell.tsx'

export const SheetTableCellGroup = ({
  row,
  column,
  rowIndex,
  columnIndex,
  getInnerCells,
  expandable,
  expanded,
  latestFieldChangesRow,
  rowHeight,
  setDragging,
  setMainCursorByRecordId,
  setRowConfig,
  expandOnDrag,
  view,
  columns,
  rootRecordNode,
  query,
  ability,
}: {
  row: ViewQueryRecordNodeWithId
  column: SheetSystemColumn<ViewQueryRecordNode> | SheetColumn<ViewQueryRecordNode>
  rowIndex: number
  columnIndex: number
  getInnerCells: (
    row: ViewQueryRecordNodeWithId,
    column: SheetSystemColumn<ViewQueryRecordNode> | SheetColumn<ViewQueryRecordNode>,
  ) => SheetCell[]
  expandable: boolean
  expanded: boolean
  latestFieldChangesRow: HashMap.HashMap<string, LatestFieldChangeItem>
  rowHeight: number
  setMainCursorByRecordId: (arguments_: SetMainCursorByRecordIdArguments) => void
  setRowConfig: (id: string, updater: (oldValue: RowConfig) => RowConfig) => void
  view: ViewFieldsFragment
  setDragging: () => void
  expandOnDrag: (position: RSheetsCursorPosition) => void
  columns: Array<SheetSystemColumn<ViewQueryRecordNode> | SheetColumn<ViewQueryRecordNode>>
  rootRecordNode: ViewQueryRecordNodeWithId
  query: ViewQueryList
  ability: ViewAbility & SalescoreObjectAbility
}): ReactElement => {
  const sheetCellGroup = getInnerCells(row, column)
  const height = getInnerRowSpan(row) * rowHeight
  const { width } = column
  const expandIconStyle = { fontSize: 10, color: RSheetsStyle.color.gray }

  return (
    <div
      className="relative"
      style={{
        width: column.width,
        transition: RSheetsStyle.cellTransition,
        backgroundColor: getHeaderColor(column.columnType === 'sheet' ? column.color : undefined).cellColor,
      }}
    >
      {/* eslint-disable-next-line array-callback-return -- ESLintの誤検知*/}
      {sheetCellGroup.map((sheetCell, index) => {
        const { recordNode, parentRecordNode, height } = sheetCell
        if (parentRecordNode === undefined && recordNode === undefined) {
          return <BlankCell key={index} width={width} height={height * rowHeight} />
        }
        switch (column.columnType) {
          case 'sheet_system': {
            const changeKey: `${string},${string},${string}` = `${recordNode?.id},${
              column.node.write?.streamName ?? column.node.name // 目標シートの時は write.streamName が undefined になる
            },`
            const fieldChange = HashMap.get(latestFieldChangesRow, changeKey).pipe(Option.getOrUndefined)
            const isFieldChanged = fieldChange !== undefined
            return (
              <SheetSystemCell
                key={index}
                sheetCell={sheetCell}
                rootRecordNode={rootRecordNode}
                column={column}
                rowIndex={rowIndex}
                columnIndex={columnIndex}
                expandable={expandable}
                expanded={expanded}
                isFieldChanged={isFieldChanged}
                rowHeight={rowHeight}
                columns={columns}
                setMainCursorByRecordId={setMainCursorByRecordId}
                setRowConfig={setRowConfig}
                view={view}
                setDragging={setDragging}
                expandOnDrag={expandOnDrag}
                ability={ability}
              />
            )
          }
          case 'sheet': {
            const changeKey: `${string},${string},${string}` = `${recordNode?.id},${
              column.node.write?.streamName ?? column.node.name // 目標シートの時は write.streamName が undefined になる
            },${column.field.name}`
            const fieldChange = HashMap.get(latestFieldChangesRow, changeKey).pipe(Option.getOrUndefined)
            const isFieldChanged = fieldChange !== undefined
            const isNewRecord = recordNode?.id?.startsWith(CORE_CONSTANT.VIEW_NEW_RECORD_PREFIX) ?? false
            return (
              <SheetValueCell
                key={index}
                sheetCell={sheetCell}
                rootRecordNode={rootRecordNode}
                column={column}
                rowIndex={rowIndex}
                columnIndex={columnIndex}
                isNewRecord={isNewRecord}
                isFieldChanged={isFieldChanged}
                overrideValue={fieldChange?.value}
                rowHeight={rowHeight}
                setMainCursorByRecordId={setMainCursorByRecordId}
                view={view}
                setDragging={setDragging}
                expandOnDrag={expandOnDrag}
                query={query}
                ability={ability}
              />
            )
          }
        }
      })}
      {sheetCellGroup.length === 0 && <BlankCell width={width} height={height} />}
      {expandable && column.node.path.length > 1 && (
        <div
          className="absolute flex w-full items-center justify-center align-middle"
          style={{
            height: 1,
            width: '100%',
            bottom: 4,
          }}
          onClick={() => {
            setRowConfig(row.id, (x) => ({ ...x, expanded: !expanded }))
          }}
        >
          {expanded ? <CaretUpOutlined style={expandIconStyle} /> : <CaretDownOutlined style={expandIconStyle} />}
        </div>
      )}
    </div>
  )
}

const BlankCell = ({ width, height }: { width: number; height: number }): ReactElement => (
  <div
    style={{
      height,
      width,
      border: `1px ${RSheetsStyle.color.border} solid`,
      background: `linear-gradient(to bottom left, transparent calc(50% - 1px), ${RSheetsStyle.color.border} calc(50% - 0.5px), ${RSheetsStyle.color.border} calc(50% + 0.5px), transparent calc(50% + 1px))`,
    }}
  />
)
